import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Phone, Mail, MapPin, CalendarDays } from "lucide-react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import {
  fadeInTitleTopVariant,
  fadeInLeftVariant,
  fadeInRightVariant,
} from "../utils/variants";

const Contact = () => {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [trainingType, setTrainingType] = useState("");

  const { t } = useTranslation();

  const failMessage = (message) => {
    let formMess = document.querySelector(".form-message");
    formMess.innerHTML = message;
    formMess.style.textAlign = "center";
    formMess.style.opacity = "1";
    formMess.style.display = "block";
    formMess.style.background = "#f71b1bff";
    formMess.style.padding = "0.8rem 0.2rem";

    document.getElementById("name").classList.add("error");
    document.getElementById("email").classList.add("error");
    document.getElementById("message").classList.add("error");

    setTimeout(() => {
      formMess.style.opacity = "0";
      formMess.style.display = "none";
    }, 5000);
  };

  const successMessage = () => {
    let formMess = document.querySelector(".form-message");
    formMess.innerHTML =
      "Message envoyé ! Nous vous recontacterons dès que possible.";
    formMess.style.background = "#4717c9ff";
    formMess.style.textAlign = "center";
    formMess.style.opacity = "1";
    formMess.style.display = "block";
    formMess.style.padding = "0.8rem 0.2rem";

    document.getElementById("name").classList.remove("error");
    document.getElementById("email").classList.remove("error");
    document.getElementById("message").classList.remove("error");

    setTimeout(() => {
      formMess.style.opacity = "0";
      formMess.style.display = "none";
    }, 5000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (name && company && email && subject && trainingType && message) {
      sendFeedback(
        "template_x9xs608",
        { name, company, phone, email, message, trainingType },
        "user_zRaqybscjuiyJtQnEFUfR",
      );
    } else {
      failMessage("Merci de remplir correctement les champs requis");
    }
  };

  const sendFeedback = (templateId, variables, publicKey) => {
    emailjs
      .send("service_z48aqdf", templateId, variables, publicKey)
      .then(() => {
        successMessage();
        setName("");
        setCompany("");
        setPhone("");
        setEmail("");
        setMessage("");
      })
      .catch(() => {
        failMessage("Une erreur s'est produite, veuillez réessayer.");
      });
  };

  return (
    <MainContainer>
      <motion.h1
        variants={fadeInTitleTopVariant}
        initial="hidden"
        whileInView="visible"
      >
        {t("contact.title")}
      </motion.h1>
      <SectionContainer>
        <ArticleText
          as={motion.article}
          variants={fadeInRightVariant}
          initial="hidden"
          whileInView="visible"
          transition={{ type: "tween", duration: 1 }}
        >
          <DivText>{t("contact.description")}</DivText>

          <CalendlyCta
            href="https://cal.com/johane-omiscar/premier-echange"
            target="_blank"
            rel="noopener noreferrer"
          >
            <CalendarDays size={18} />
            <span>{t("contact.bookCall")}</span>
          </CalendlyCta>

          <DivEmail>
            <div className="icon-wrapper">
              <Phone size={20} className="icon" />
            </div>
            <EmailList>
              <EmailText>{t("contact.phone")}</EmailText>
              <EmailValue>07.45.08.29.97</EmailValue>
            </EmailList>
          </DivEmail>

          <DivEmail>
            <div className="icon-wrapper">
              <Mail size={20} className="icon" />
            </div>
            <EmailList>
              <EmailText>Email</EmailText>
              <EmailValue>JOHANE.OMISCAR@GMAIL.COM</EmailValue>
            </EmailList>
          </DivEmail>

          <DivEmail>
            <div className="icon-wrapper">
              <MapPin size={20} className="icon" />
            </div>
            <EmailList>
              <EmailText>{t("contact.place")}</EmailText>
              <EmailValue>{t("contact.zone")}</EmailValue>
            </EmailList>
          </DivEmail>
        </ArticleText>

        <SectionForm
          as={motion.section}
          variants={fadeInLeftVariant}
          initial="hidden"
          whileInView="visible"
          transition={{ type: "tween", duration: 1 }}
        >
          <FormContainer method="POST">
            <h2 className="form-title">{t("contact.quote")}</h2>

            <DivLabel>
              <div>
                <LabelInput htmlFor="name">
                  {t("contact.name")}, {t("contact.firstname")}
                </LabelInput>
                <InputGrid
                  type="text"
                  id="name"
                  name="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div>
                <LabelInput htmlFor="company">
                  {t("contact.company")}
                </LabelInput>
                <InputGrid
                  type="text"
                  id="company"
                  name="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </div>

              <div>
                <LabelInput htmlFor="email">Email *</LabelInput>
                <InputGrid
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div>
                <LabelInput htmlFor="phone">{t("contact.phone")}</LabelInput>
                <InputGrid
                  type="tel"
                  id="phone"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </DivLabel>

            <div>
              <LabelInput htmlFor="subject">{t("contact.subject")}</LabelInput>
              <InputSubject
                type="text"
                id="subject"
                name="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <FormGroup>
              <LabelInput htmlFor="trainingType">
                {t("contact.training")}
              </LabelInput>
              <Select
                id="trainingType"
                name="trainingType"
                value={trainingType}
                onChange={(e) => setTrainingType(e.target.value)}
                required
              >
                <option value="">{t("contact.select")}</option>
                <option value="dev-mvp">{t("contact.opt1")}</option>
                <option value="cadrage-archi">{t("contact.opt2")}</option>
                <option value="lead-projet">{t("contact.opt3")}</option>
                <option value="formation">{t("contact.opt4")}</option>
                <option value="automatisation-ia">{t("contact.opt5")}</option>
                <option value="accessibilite-qualite">
                  {t("contact.opt6")}
                </option>
                <option value="sur-mesure">{t("contact.opt7")}</option>
                <option value="autre">{t("contact.opt8")}</option>
              </Select>
            </FormGroup>

            <div>
              <LabelInput htmlFor="message">Message</LabelInput>
              <TextZone
                id="message"
                name="message"
                rows="6"
                placeholder={t("contact.placeholder")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            <div className="form-message"></div>

            <DivBtn>
              <Btn type="submit" onClick={handleSubmit}>
                {t("contact.send")}
              </Btn>
            </DivBtn>
          </FormContainer>
        </SectionForm>
      </SectionContainer>
    </MainContainer>
  );
};

export default Contact;

/* ─── Layout ──────────────────────────────────────────────────────────────── */

const MainContainer = styled.main`
  color: #fafafa;
  width: 83%;
  margin: 10rem auto 7rem auto;
  padding: 2rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);

  /* Suppression du height: 200vh sur mobile — la hauteur doit être auto */
  @media (max-width: 768px) {
    width: 95%;
    margin: 6rem auto 4rem auto;
    padding: 1.25rem;
    border-radius: 14px;
  }
`;

const SectionContainer = styled.section`
  max-width: 90%;
  margin: auto;
  display: grid;
  grid-template-columns: 1fr 450px;
  gap: 6rem;
  align-items: start; /* start évite l'étirement vertical */
  padding: 3rem 0;

  /* Grand écran */
  @media (min-width: 1900px) {
    grid-template-columns: 1fr 1fr;
    gap: 8rem;
  }

  /* Tablette paysage */
  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }

  /* Tablette portrait + mobile */
  @media (max-width: 768px) {
    max-width: 100%;
    grid-template-columns: 1fr;
    gap: 2.5rem;
    padding: 1.5rem 0;
  }
`;

/* ─── Colonne gauche (infos) ──────────────────────────────────────────────── */

const ArticleText = styled.article`
  padding: 2rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(6px);

  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 14px;
  }
`;

const DivText = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2.5rem;
  text-align: center;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (min-width: 1900px) {
    font-size: 2.2rem;
  }
`;

const CalendlyCta = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1rem 0 2rem 0;
  padding: 0.7rem 1.5rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fafafa;
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(180, 1, 18, 0.2);
    border-color: rgba(180, 1, 18, 0.6);
    transform: translateY(-2px);
  }
`;

const DivEmail = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 0.8rem 1rem;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }

  .icon-wrapper {
    flex-shrink: 0; /* empêche l'icône de se réduire */
    width: 35px;
    height: 35px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: rgba(180, 1, 18, 0.15);
    box-shadow: 0 5px 15px rgba(180, 1, 18, 0.2);
  }

  .icon {
    color: #fff;
  }

  @media (max-width: 480px) {
    padding: 0.6rem 0.5rem;
    gap: 0.75rem;
  }
`;

const EmailList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  min-width: 0; /* important pour que le texte long puisse se couper */
`;

const EmailText = styled.li`
  text-transform: uppercase;
  font-family: "Grechen Fuemen", cursive;
  font-size: 1.2rem;

  @media (max-width: 480px) {
    font-size: 1rem;
  }

  @media (min-width: 1900px) {
    font-size: 2rem;
  }
`;

const EmailValue = styled.li`
  color: #a8a29e;
  font-family: "Gowun Batang", serif;
  word-break: break-all; /* évite le débordement des adresses email longues */

  @media (max-width: 480px) {
    font-size: 0.85rem;
  }
`;

/* ─── Colonne droite (formulaire) ────────────────────────────────────────── */

const SectionForm = styled.section`
  /* Pas de largeur fixe ici — c'est la grille qui gère */
`;

const FormContainer = styled.form`
  width: 100%; /* fluide à l'intérieur de la colonne */

  h2 {
    margin-bottom: 1.5rem;

    @media (min-width: 1900px) {
      font-size: 2.5rem;
    }
  }
`;

const DivLabel = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 1rem;

  @media (max-width: 480px) {
    /* Une colonne sur petits écrans */
    grid-template-columns: 1fr;
  }

  @media (min-width: 1900px) {
    gap: 0 2rem;
  }
`;

const LabelInput = styled.label`
  display: block;
  padding-bottom: 0.2rem;
  font-size: 0.9rem;

  @media (min-width: 1900px) {
    font-size: 2rem;
  }
`;

/* Input de base : toujours 100 % de son conteneur */
const InputBase = styled.input`
  width: 100%;
  box-sizing: border-box; /* inclut padding dans la largeur */
  padding: 0.6rem 0.8rem;
  margin-bottom: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #fff;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(180, 1, 18, 0.6);
    box-shadow: 0 0 0 3px rgba(180, 1, 18, 0.1);
    background: rgba(255, 255, 255, 0.06);
  }

  &.error {
    border-color: #f71b1b;
  }

  @media (min-width: 1900px) {
    font-size: 1.4rem;
    padding: 0.9rem 1rem;
    margin-bottom: 2rem;
  }
`;

/* InputGrid hérite de InputBase sans largeur fixe */
const InputGrid = styled(InputBase)``;

/* InputSubject : pleine largeur, pas de taille fixe en rem */
const InputSubject = styled(InputBase)``;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 1rem;

  @media (min-width: 1900px) {
    margin-bottom: 2.5rem;
  }
`;

export const Select = styled.select`
  width: 100%;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 0.9rem 1rem;
  color: #fff;
  font-size: 0.85rem;
  transition: all 0.3s ease;

  option {
    background: #1a1a1a;
    color: #fff;
  }

  &:focus {
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 0 3px rgba(255, 71, 87, 0.1);
  }

  @media (min-width: 1900px) {
    font-size: 1.4rem;
  }
`;

const TextZone = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  padding: 1rem;
  margin-bottom: 1rem;
  resize: vertical; /* permet le redimensionnement vertical uniquement */
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  color: #fff;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgba(180, 1, 18, 0.6);
    box-shadow: 0 0 0 3px rgba(180, 1, 18, 0.1);
  }

  &.error {
    border-color: #f71b1b;
  }

  @media (min-width: 1900px) {
    font-size: 1.4rem;
  }
`;

const DivBtn = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;

  @media (min-width: 1900px) {
    margin-top: 1.5rem;
  }
`;

const Btn = styled.button`
  cursor: pointer;
  color: #fafafa;
  font-size: 1rem;
  padding: 0.6rem 2rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 999px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(180, 1, 18, 0.2);
    border-color: rgba(180, 1, 18, 0.6);
    transform: translateY(-2px);
  }

  @media (min-width: 1900px) {
    font-size: 2rem;
    padding: 0.8rem 3rem;
  }
`;
